import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Badge, Button, Card, Col, Container, Form, InputGroup, Modal, Row, Spinner, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  fetchHotelAvailability,
  markHotelSoldOut as markHotelSoldOutApi,
  undoHotelSoldOut as undoHotelSoldOutApi,
  updateHotelAvailability,
} from "../services/hotelAvailabilityService";
import HotelOwnerAccessManager from "./HotelOwnerAccessManager";
import PendingHotelAcceptances from "./PendingHotelAcceptances";
import RoomHoldsManager from "./RoomHoldsManager";
import IsoDatePicker from "../components/IsoDatePicker";
import { formatDateDMY } from "../utils/dateFormat";

const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const todayValue = () => formatDate(new Date());
const maximumDateValue = () => addDays(todayValue(), 89);

const addDays = (dateValue, days) => {
  const date = new Date(`${dateValue}T00:00:00`);
  date.setDate(date.getDate() + days);
  return formatDate(date);
};

const mergeAvailabilityUpdates = (currentHotels, updatedHotels) => {
  if (!Array.isArray(updatedHotels) || updatedHotels.length === 0) return currentHotels;
  const updatedDaysByCategory = new Map();
  updatedHotels.forEach((hotel) => {
    (hotel.roomCategories || []).forEach((category) => {
      const daysByDate = new Map();
      (category.days || []).forEach((day) => daysByDate.set(day.date, day));
      updatedDaysByCategory.set(String(category.roomCategoryId), {
        category,
        daysByDate,
      });
    });
  });

  return currentHotels.map((hotel) => ({
    ...hotel,
    roomCategories: (hotel.roomCategories || []).map((category) => {
      const updated = updatedDaysByCategory.get(String(category.roomCategoryId));
      if (!updated) return category;
      return {
        ...category,
        availableUnits: updated.category.availableUnits ?? category.availableUnits,
        days: (category.days || []).map((day) => updated.daysByDate.get(day.date) || day),
      };
    }),
  }));
};

const formatShortDayLabel = (isoDate) => {
  const date = new Date(`${isoDate}T00:00:00`);
  const dayMonth = date.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
  const weekday = date.toLocaleDateString("en-IN", { weekday: "short" });
  return { dayMonth, weekday };
};

const formatSummaryRange = (firstIsoDate, lastIsoDate) => {
  const first = new Date(`${firstIsoDate}T00:00:00`);
  const last = new Date(`${lastIsoDate}T00:00:00`);
  const firstLabel = first.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
  const lastLabel = last.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  return firstIsoDate === lastIsoDate ? lastLabel : `${firstLabel} - ${lastLabel}`;
};

const summaryHotelLabel = (hotel) => {
  const code = (hotel.hotelCode || "").trim();
  const name = (hotel.hotelName || "").trim();
  if (!code) return name || "Hotel";
  if (!name) return code;
  if (name.toLowerCase().includes(code.toLowerCase())) return name;
  if (code.toLowerCase().includes(name.toLowerCase())) return code;
  return `${code} - ${name}`;
};

// The guest's name goes before the booking id: the caretaker checks people in by name, and the id
// only matters when they call us about it.
const bookingReferenceText = (row, fallback) => {
  if (row.bookingId == null) return `  _${fallback}_`;
  const name = (row.customerName || "").trim();
  return `  _${name ? `${name} · ` : ""}Booking Id: ${row.bookingId}_`;
};

const buildBookingSummaryText = (hotel) => {
  const dateEntries = new Map();
  const bookingIds = new Set();
  let totalRoomNights = 0;

  (hotel.roomCategories || []).forEach((category) => {
    (category.days || []).forEach((day) => {
      const sold = Number(day.soldToIotripsUnits || 0);
      if (sold <= 0) return;
      totalRoomNights += sold;
      const rows = dateEntries.get(day.date) || [];
      // One line per booking rather than per category: the hotel reads this to know who is in
      // which room, so collapsing two bookings into "2 rooms" loses the thing they need.
      (day.iotripsBookings || []).forEach((booking) => {
        const units = Number(booking.units || 0);
        if (units <= 0) return;
        if (booking.bookingId != null) bookingIds.add(booking.bookingId);
        rows.push({
          categoryName: category.categoryName,
          units,
          roomNumbers: (booking.roomNumbers || []).filter(Boolean),
          bookingId: booking.bookingId,
          customerName: booking.customerName,
        });
      });
      dateEntries.set(day.date, rows);
    });
  });

  if (totalRoomNights === 0) return null;

  const sortedDates = Array.from(dateEntries.keys()).sort();
  // The hotel knows its own name; our internal code is noise in a message addressed to them.
  const hotelLabel = (hotel.hotelName || "").trim() || summaryHotelLabel(hotel);
  const city = (hotel.city || "").trim();

  // WhatsApp markup: *bold* for the things a caretaker scans for - the date and the room number -
  // and _italic_ for the booking reference, which only matters when they call us about it.
  const lines = [
    `*${hotelLabel} - Room Booking Summary*`,
    `📅 ${formatSummaryRange(sortedDates[0], sortedDates[sortedDates.length - 1])}${city ? `   📍 ${city}` : ""}`,
    "",
  ];

  sortedDates.forEach((date) => {
    const { dayMonth, weekday } = formatShortDayLabel(date);
    lines.push(`*${weekday}, ${dayMonth}*`);
    const rows = [...dateEntries.get(date)].sort(
      (a, b) =>
        String(a.roomNumbers[0] || "").localeCompare(String(b.roomNumbers[0] || ""), undefined, { numeric: true })
        || String(a.categoryName || "").localeCompare(String(b.categoryName || "")),
    );
    rows.forEach((row) => {
      // With room numbers the count is implied by the list, so show the numbers instead.
      const lead = row.roomNumbers.length
        ? `*Room${row.roomNumbers.length === 1 ? "" : "s"} ${row.roomNumbers.join(", ")}*`
        : `*${row.units} room${row.units === 1 ? "" : "s"}*`;
      const ref = bookingReferenceText(row, "(unassigned)");
      // WhatsApp keeps leading spaces, so the rooms sit indented under their date heading.
      lines.push(`   • ${lead} - ${row.categoryName}${ref}`);
    });
    lines.push("");
  });

  const bookingCount = bookingIds.size;
  lines.push("────────────────────");
  lines.push(
    `*Total: ${totalRoomNights} room-night${totalRoomNights === 1 ? "" : "s"}*`
    + (bookingCount ? ` · ${bookingCount} booking${bookingCount === 1 ? "" : "s"}` : ""),
  );
  lines.push("");
  lines.push("Please confirm the above rooms.");
  lines.push("Thank you!");
  lines.push("*IO Trips*");
  return lines.join("\n");
};

// Indexes a hotel's nights as date -> "categoryId|bookingId" -> { units, roomNumbers }, which is
// the shape the already-checked-in test below needs. An allocation with no booking id cannot be
// matched to the night before with any confidence, so it is keyed by its own date: that key can
// never appear on the previous night, which makes the night count as new rather than covered.
const indexHotelNights = (hotel) => {
  const byDate = new Map();
  (hotel.roomCategories || []).forEach((category) => {
    (category.days || []).forEach((day) => {
      (day.iotripsBookings || []).forEach((booking) => {
        const units = Number(booking.units || 0);
        if (units <= 0) return;
        const key = booking.bookingId == null
          ? `${category.roomCategoryId}|unassigned@${day.date}`
          : `${category.roomCategoryId}|${booking.bookingId}`;
        const allocations = byDate.get(day.date) || new Map();
        const existing = allocations.get(key);
        const roomNumbers = (booking.roomNumbers || []).filter(Boolean).map(String);
        // One booking can appear more than once on a night; fold them into a single allocation.
        allocations.set(key, {
          units: (existing?.units || 0) + units,
          roomNumbers: new Set([...(existing?.roomNumbers || []), ...roomNumbers]),
        });
        byDate.set(day.date, allocations);
      });
    });
  });
  return byDate;
};

// True when the night before already held this booking's rooms in this category: at least as many
// rooms, and every room number it has tonight.
const allocationWasHeld = (previous, key, allocation) => {
  const held = previous?.get(key);
  if (!held || held.units < allocation.units) return false;
  // An unassigned room tonight is data-entry lag, not a room move, so only assigned numbers
  // have to have been held already.
  return Array.from(allocation.roomNumbers).every((room) => held.roomNumbers.has(room));
};

// True when the hotel is already holding everything this night needs, because the same booking
// occupied the same room category the night before with at least as many rooms and the same room
// numbers. A guest who is physically in the room cannot have it given away, which is the only job
// the reminder has. Anything that changes overnight - a room move, a category change, an extra
// room, a gap in the stay, a different booking arriving - fails this test and still gets sent.
const nightIsAlreadyCheckedIn = (nights, date) => {
  const allocations = nights.get(date);
  if (!allocations || !allocations.size) return true;
  const previous = nights.get(addDays(date, -1));
  if (!previous || !previous.size) return false;
  return Array.from(allocations.entries())
    .every(([key, allocation]) => allocationWasHeld(previous, key, allocation));
};

// A hotel is worth messaging when any night in the range is not already covered by the night
// before it. Nights after the first compare against a night inside the range; the first compares
// against the extra baseline day fetched ahead of the range.
const hotelHasNewArrivals = (hotel, fromDate) => {
  const nights = indexHotelNights(hotel);
  return Array.from(nights.keys())
    .filter((date) => !fromDate || date >= fromDate)
    .some((date) => !nightIsAlreadyCheckedIn(nights, date));
};

// True when this booking has held these rooms without a break since before the range started, i.e.
// the guest is already in the room when the reminder goes out. Comparing only with the night before
// is not enough: a guest arriving inside the range holds the same rooms on their second night too,
// and dropping that night would tell the hotel they leave after one.
const wasInRoomBeforeRange = (nights, key, date, fromDate) => {
  if (!fromDate) return false;
  for (let night = date; night >= fromDate; night = addDays(night, -1)) {
    const tonight = nights.get(night)?.get(key);
    if (!tonight || !allocationWasHeld(nights.get(addDays(night, -1)), key, tonight)) return false;
  }
  return true;
};

// The reminder a hotel gets the day before arrival. Deliberately not the confirmation message
// above: by this point the rooms are already agreed, and the only job left is making sure nobody
// gives them away. So it states the rooms plainly and asks for nothing back.
//
// With skipCheckedIn, a guest's night is left out when they were already in the same rooms before
// the range started: there is nothing to remind the hotel about. Guests arriving inside the range
// keep every night of their stay, and room changes and extra rooms still show.
const buildBookingReminderText = (hotel, fromDate, skipCheckedIn = false) => {
  const dateEntries = new Map();
  const bookingIds = new Set();
  let totalRoomNights = 0;
  const nights = skipCheckedIn ? indexHotelNights(hotel) : null;

  (hotel.roomCategories || []).forEach((category) => {
    (category.days || []).forEach((day) => {
      // We deliberately fetch one day before the range as a baseline for the check-in test below.
      // It exists only for comparison and must never reach the hotel's message.
      if (fromDate && day.date < fromDate) return;
      const sold = Number(day.soldToIotripsUnits || 0);
      if (sold <= 0) return;
      const rows = dateEntries.get(day.date) || [];
      (day.iotripsBookings || []).forEach((booking) => {
        const units = Number(booking.units || 0);
        if (units <= 0) return;
        if (nights && booking.bookingId != null
          && wasInRoomBeforeRange(nights, `${category.roomCategoryId}|${booking.bookingId}`, day.date, fromDate)) {
          return;
        }
        totalRoomNights += units;
        if (booking.bookingId != null) bookingIds.add(booking.bookingId);
        rows.push({
          categoryName: category.categoryName,
          units,
          roomNumbers: (booking.roomNumbers || []).filter(Boolean),
          bookingId: booking.bookingId,
          customerName: (booking.customerName || "").trim(),
        });
      });
      if (rows.length) dateEntries.set(day.date, rows);
    });
  });

  if (totalRoomNights === 0) return null;

  const sortedDates = Array.from(dateEntries.keys()).sort();
  const hotelLabel = (hotel.hotelName || "").trim() || summaryHotelLabel(hotel);
  const city = (hotel.city || "").trim();
  const singleDay = sortedDates.length === 1;

  const lines = [
    "*IO Trips – Booking Reminder*",
    `*${hotelLabel}*${city ? `   📍 ${city}` : ""}`,
    `📅 ${formatSummaryRange(sortedDates[0], sortedDates[sortedDates.length - 1])}`,
    "",
    singleDay
      ? "A reminder of the rooms we have booked with you for this date:"
      : "A reminder of the rooms we have booked with you on these dates:",
    "",
  ];

  sortedDates.forEach((date) => {
    const { dayMonth, weekday } = formatShortDayLabel(date);
    lines.push(`*${weekday}, ${dayMonth}*`);
    const rows = [...dateEntries.get(date)].sort(
      (a, b) =>
        String(a.roomNumbers[0] || "").localeCompare(String(b.roomNumbers[0] || ""), undefined, { numeric: true })
        || String(a.categoryName || "").localeCompare(String(b.categoryName || "")),
    );
    // Grouped by guest within the day, so a guest with several rooms is named once rather than on
    // every room line. Guests keep the order of their first room; unassigned rooms go last.
    const guests = new Map();
    rows.forEach((row) => {
      const key = row.bookingId == null ? "unassigned" : String(row.bookingId);
      const guest = guests.get(key) || { bookingId: row.bookingId, customerName: row.customerName, rooms: [] };
      guest.rooms.push(row);
      guests.set(key, guest);
    });
    Array.from(guests.values())
      .sort((a, b) => (a.bookingId == null) - (b.bookingId == null))
      .forEach((guest) => {
        lines.push(guest.bookingId == null
          ? "   👤 *Other rooms*  _(guest details to follow)_"
          : `   👤 *${guest.customerName || "IO Trips guest"}*  _Booking Id: ${guest.bookingId}_`);
        guest.rooms.forEach((row) => {
          const lead = row.roomNumbers.length
            ? `Room${row.roomNumbers.length === 1 ? "" : "s"} ${row.roomNumbers.join(", ")}`
            : `${row.units} room${row.units === 1 ? "" : "s"}`;
          lines.push(`      • ${lead} - ${row.categoryName}`);
        });
      });
    lines.push("");
  });

  const bookingCount = bookingIds.size;
  const totalLabel = singleDay
    ? `*Total: ${totalRoomNights} room${totalRoomNights === 1 ? "" : "s"}*`
    : `*Total: ${totalRoomNights} room-night${totalRoomNights === 1 ? "" : "s"}*`;
  lines.push("────────────────────");
  lines.push(totalLabel + (bookingCount ? ` · ${bookingCount} booking${bookingCount === 1 ? "" : "s"}` : ""));
  lines.push("");
  lines.push("*No reply needed* - this is only so the rooms stay blocked for our guests.");
  lines.push("If anything here does not match your register, please call us right away.");
  lines.push("");
  lines.push("Thank you!");
  lines.push("*IO Trips*");
  return lines.join("\n");
};

// WhatsApp wants digits only with a country code. Caretaker numbers are typed in by hand, so
// anything not recognisably an Indian mobile is left to a manual copy rather than dialled wrongly.
const reminderWhatsappNumber = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length === 10) return `91${digits}`;
  if (digits.length === 11 && digits.startsWith("0")) return `91${digits.slice(1)}`;
  if (digits.length === 12 && digits.startsWith("91")) return digits;
  return "";
};

const SUMMARY_RANGE_PRESETS = [
  // The reminder goes out the day before arrival, so that is the first and default option.
  { id: "TOMORROW", label: "Tomorrow", days: 1, startOffset: 1 },
  { id: "TODAY", label: "Today", days: 1, startOffset: 0 },
  { id: "WEEK", label: "Next 1 Week", days: 7 },
  { id: "FIFTEEN_DAYS", label: "Next 15 Days", days: 15 },
  { id: "MONTH", label: "Next Month", days: 30 },
  { id: "THREE_MONTHS", label: "Next 3 Months", days: 90 },
  { id: "CUSTOM", label: "Custom Range", days: null },
];

const copyTextToClipboard = async (text) => {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
};

const roomNumbersText = (booking) => {
  const numbers = (booking?.roomNumbers || []).filter(Boolean);
  return numbers.length ? numbers.join(", ") : "";
};

const SoldUnitsLabel = ({ count, label, bookings }) => {
  const items = Array.isArray(bookings) ? bookings.filter((item) => Number(item?.units || 0) > 0) : [];
  return (
    <div className="sold-units-details">
      <div className="sold-units-label">{count} {label}</div>
      {items.map((item, index) => {
        const bookingReference = item.bookingId ? `Booking #${item.bookingId}` : (item.label || "Booking");
        const rooms = `${item.units} room${Number(item.units) === 1 ? "" : "s"}`;
        const roomNumbers = roomNumbersText(item);
        return (
          <div className="sold-booking-reference" key={item.bookingId ?? `${item.label}-${index}`}>
            <span>{bookingReference}</span>
            <span>{rooms}{roomNumbers ? ` - Room ${roomNumbers}` : ""}</span>
          </div>
        );
      })}
    </div>
  );
};

export default function HotelAvailabilityAdmin({
  title = "Hotel Room Availability",
  subtitle = "Manage sold and available rooms date-wise",
  showOwnerAccess = true,
  showSoldOutActions = true,
  showCopySummary = true,
  directManagementView = false,
  // View only (Call / Chat team): no update form, and date cells are plain text, not edit buttons.
  readOnly = false,
  fetchAvailability = fetchHotelAvailability,
  updateAvailability = updateHotelAvailability,
  markHotelSoldOut = markHotelSoldOutApi,
  undoHotelSoldOut = undoHotelSoldOutApi,
  onUnauthorized,
  headerAction,
}) {
  const editFormRef = useRef(null);
  const [startDate, setStartDate] = useState(todayValue());
  const [endDate, setEndDate] = useState(addDays(todayValue(), 14));
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [quickSaving, setQuickSaving] = useState("");
  const [quickEdits, setQuickEdits] = useState({});
  const [searchHotelId, setSearchHotelId] = useState("");
  const [collapsedCities, setCollapsedCities] = useState({});
  const [collapsedRoomCategories, setCollapsedRoomCategories] = useState({});
  // The hotel the "Hold Rooms" form is open for (admin only).
  const [holdCreateHotel, setHoldCreateHotel] = useState(null);
  const [editSectionOverride, setEditSectionOverride] = useState(null);
  const [selectedHotelId, setSelectedHotelId] = useState("");
  const [edit, setEdit] = useState({
    roomCategoryId: "",
    startDate: todayValue(),
    endDate: addDays(todayValue(), 1),
    soldUnits: 0,
    soldTo: "OTHERS",
    note: "",
  });

  const loadAvailability = useCallback(async (rangeStart = startDate, rangeEnd = endDate) => {
    setLoading(true);
    try {
      setHotels(await fetchAvailability(rangeStart, rangeEnd));
    } catch (error) {
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        onUnauthorized?.();
      }
      toast.error(error?.response?.data?.message || "Failed to load hotel availability");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, fetchAvailability, onUnauthorized]);

  useEffect(() => {
    loadAvailability(startDate, endDate);
  }, [loadAvailability, startDate, endDate]);

  const manyHotels = hotels.length !== 1;
  const editSectionExpanded = editSectionOverride ?? !manyHotels;

  const [summaryModal, setSummaryModal] = useState(null);
  const [summaryPreset, setSummaryPreset] = useState("FIFTEEN_DAYS");
  const [reminderSelectedIds, setReminderSelectedIds] = useState([]);
  const [reminderResults, setReminderResults] = useState(null);
  const [reminderSkipCheckedIn, setReminderSkipCheckedIn] = useState(true);
  const [summaryStartDate, setSummaryStartDate] = useState(todayValue());
  const [summaryEndDate, setSummaryEndDate] = useState(addDays(todayValue(), 14));
  const [summaryLoading, setSummaryLoading] = useState(false);

  const [soldOutModal, setSoldOutModal] = useState(null);
  const [soldOutStartDate, setSoldOutStartDate] = useState(todayValue());
  const [soldOutEndDate, setSoldOutEndDate] = useState(addDays(todayValue(), 1));
  const [soldOutNote, setSoldOutNote] = useState("");
  const [soldOutSaving, setSoldOutSaving] = useState(false);

  const openSummaryModal = useCallback((hotel) => {
    setSummaryModal({ hotel });
    setSummaryPreset("FIFTEEN_DAYS");
    setSummaryStartDate(todayValue());
    setSummaryEndDate(addDays(todayValue(), 14));
  }, []);

  // Tomorrow by default: the point of this message is to reach the hotel the day before arrival.
  const openReminderModal = useCallback(() => {
    const start = addDays(todayValue(), 1);
    setSummaryModal({ mode: "REMINDER" });
    setSummaryPreset("TOMORROW");
    setSummaryStartDate(start);
    setSummaryEndDate(start);
    setReminderSelectedIds(hotels.map((hotel) => String(hotel.hotelId)));
    setReminderResults(null);
    setReminderSkipCheckedIn(true);
  }, [hotels]);

  const closeSummaryModal = useCallback(() => {
    if (summaryLoading) return;
    setSummaryModal(null);
    setReminderResults(null);
  }, [summaryLoading]);

  const toggleReminderHotel = useCallback((hotelId) => {
    const key = String(hotelId);
    setReminderSelectedIds((previous) => (previous.includes(key)
      ? previous.filter((item) => item !== key)
      : [...previous, key]));
  }, []);

  const handleGenerateReminders = useCallback(async () => {
    if (!summaryStartDate || !summaryEndDate || summaryEndDate < summaryStartDate) {
      toast.error("End date must be on or after the start date");
      return;
    }
    if (!reminderSelectedIds.length) {
      toast.error("Select at least one hotel");
      return;
    }
    setSummaryLoading(true);
    try {
      // One day before the range is fetched purely so the first night can be compared against the
      // night before it. buildBookingReminderText drops it, so it never appears in a message.
      let rows;
      let baselineLoaded = true;
      try {
        rows = await fetchAvailability(addDays(summaryStartDate, -1), summaryEndDate);
      } catch {
        // A backend that still refuses any start date before today cannot give us the night
        // before. Fall back to the range on its own and send to everyone, rather than failing
        // outright or - worse - skipping hotels on a comparison we could not actually make.
        rows = await fetchAvailability(summaryStartDate, summaryEndDate);
        baselineLoaded = false;
      }
      const skipCheckedIn = reminderSkipCheckedIn && baselineLoaded;
      const selected = new Set(reminderSelectedIds.map(String));
      const withBookings = [];
      const withoutBookings = [];
      const alreadyCheckedIn = [];
      (rows || [])
        .filter((row) => selected.has(String(row.hotelId)))
        .forEach((row) => {
          const fullText = buildBookingReminderText(row, summaryStartDate);
          const text = skipCheckedIn ? buildBookingReminderText(row, summaryStartDate, true) : fullText;
          if (!fullText) {
            withoutBookings.push(row);
          } else if (!text || (skipCheckedIn && !hotelHasNewArrivals(row, summaryStartDate))) {
            // !text: every night left after dropping in-house guests was empty, so there is nothing to send.
            alreadyCheckedIn.push(row);
          } else {
            withBookings.push({ hotel: row, text });
          }
        });
      if (!withBookings.length) {
        toast.info(alreadyCheckedIn.length
          ? "Every selected hotel already has these guests checked in - nothing to remind."
          : "No IO Trips bookings for the selected hotels in this date range.");
        return;
      }
      // Sorted by city so the person works down the list the way the page is laid out.
      withBookings.sort((a, b) =>
        String(a.hotel.city || "").localeCompare(String(b.hotel.city || ""))
        || String(a.hotel.hotelName || "").localeCompare(String(b.hotel.hotelName || "")));
      alreadyCheckedIn.sort((a, b) =>
        String(a.city || "").localeCompare(String(b.city || ""))
        || String(a.hotelName || "").localeCompare(String(b.hotelName || "")));
      setReminderResults({
        items: withBookings,
        skipped: withoutBookings.length,
        alreadyCheckedIn,
        checkedInUnavailable: reminderSkipCheckedIn && !baselineLoaded,
      });
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not generate booking reminders");
    } finally {
      setSummaryLoading(false);
    }
  }, [summaryStartDate, summaryEndDate, reminderSelectedIds, reminderSkipCheckedIn, fetchAvailability]);

  const isReminderMode = summaryModal?.mode === "REMINDER";
  const reminderHotelGroups = useMemo(() => {
    const byCity = new Map();
    hotels.forEach((hotel) => {
      const city = (hotel.city || "").trim() || "Other";
      if (!byCity.has(city)) byCity.set(city, []);
      byCity.get(city).push(hotel);
    });
    return Array.from(byCity.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([city, cityHotels]) => ({
        city,
        hotels: [...cityHotels].sort((a, b) =>
          String(a.hotelName || a.hotelCode || "").localeCompare(String(b.hotelName || b.hotelCode || ""))),
      }));
  }, [hotels]);

  const copyReminder = useCallback(async (item) => {
    try {
      await copyTextToClipboard(item.text);
      toast.success(`Reminder copied for ${item.hotel.hotelName || item.hotel.hotelCode}`);
    } catch {
      toast.error("Could not copy the reminder");
    }
  }, []);

  const handleSummaryPresetChange = useCallback((presetId) => {
    setSummaryPreset(presetId);
    const preset = SUMMARY_RANGE_PRESETS.find((item) => item.id === presetId);
    if (preset?.days) {
      const start = addDays(todayValue(), preset.startOffset || 0);
      setSummaryStartDate(start);
      setSummaryEndDate(addDays(start, preset.days - 1));
    }
  }, []);

  const handleGenerateSummary = useCallback(async () => {
    const hotel = summaryModal?.hotel;
    if (!hotel) return;
    if (!summaryStartDate || !summaryEndDate || summaryEndDate < summaryStartDate) {
      toast.error("End date must be on or after the start date");
      return;
    }
    setSummaryLoading(true);
    try {
      const rows = await fetchAvailability(summaryStartDate, summaryEndDate);
      const hotelRow = (rows || []).find((item) => String(item.hotelId) === String(hotel.hotelId));
      const text = buildBookingSummaryText(hotelRow || {});
      if (!text) {
        toast.info(`No Iotrips bookings for ${hotel.hotelCode || hotel.hotelName} in the selected date range.`);
        return;
      }
      await copyTextToClipboard(text);
      toast.success(`Booking summary copied for ${hotel.hotelCode || hotel.hotelName}`);
      setSummaryModal(null);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not generate booking summary");
    } finally {
      setSummaryLoading(false);
    }
  }, [summaryModal, summaryStartDate, summaryEndDate, fetchAvailability]);

  const openSoldOutModal = useCallback((hotel, mode = "CLOSE") => {
    // Default to a single night rather than the whole window: closing 15 days by accident is
    // far more expensive to undo than opening the modal again for a longer range.
    const firstNight = startDate < todayValue() ? todayValue() : startDate;
    setSoldOutModal({ hotel, mode });
    setSoldOutStartDate(firstNight);
    setSoldOutEndDate(addDays(firstNight, 1));
    setSoldOutNote("");
  }, [startDate]);

  const closeSoldOutModal = useCallback(() => {
    if (soldOutSaving) return;
    setSoldOutModal(null);
  }, [soldOutSaving]);

  const handleSoldOutSubmit = useCallback(async () => {
    const hotel = soldOutModal?.hotel;
    if (!hotel) return;
    if (!soldOutStartDate || !soldOutEndDate || soldOutEndDate <= soldOutStartDate) {
      toast.error("Check-out must be after check-in");
      return;
    }
    const reopening = soldOutModal.mode === "REOPEN";
    const hotelLabel = hotel.hotelCode || hotel.hotelName;
    setSoldOutSaving(true);
    try {
      const payload = {
        hotelId: Number(hotel.hotelId),
        startDate: soldOutStartDate,
        endDate: soldOutEndDate,
      };
      const updatedAvailability = reopening
        ? await undoHotelSoldOut(payload)
        : await markHotelSoldOut({ ...payload, note: soldOutNote.trim() });
      setHotels((current) => mergeAvailabilityUpdates(current, updatedAvailability));
      await loadAvailability(startDate, endDate);
      toast.success(
        reopening ? `${hotelLabel} reopened` : `${hotelLabel} marked sold out`,
      );
      setSoldOutModal(null);
    } catch (error) {
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        onUnauthorized?.();
      }
      toast.error(
        error?.response?.data?.message
          || (reopening ? "Could not reopen the hotel" : "Could not mark the hotel sold out"),
      );
    } finally {
      setSoldOutSaving(false);
    }
  }, [
    soldOutModal,
    soldOutStartDate,
    soldOutEndDate,
    soldOutNote,
    markHotelSoldOut,
    undoHotelSoldOut,
    loadAvailability,
    startDate,
    endDate,
    onUnauthorized,
  ]);

  const soldOutNights = useMemo(() => {
    if (!soldOutStartDate || !soldOutEndDate || soldOutEndDate <= soldOutStartDate) return 0;
    const from = new Date(`${soldOutStartDate}T00:00:00`);
    const to = new Date(`${soldOutEndDate}T00:00:00`);
    return Math.round((to - from) / 86400000);
  }, [soldOutStartDate, soldOutEndDate]);

  const hotelsByCity = useCallback((items) => {
    const groups = new Map();
    items.forEach((hotel) => {
      const city = hotel.city?.trim() || "Other";
      if (!groups.has(city)) groups.set(city, []);
      groups.get(city).push(hotel);
    });
    return Array.from(groups.entries())
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([city, cityHotels]) => ({
        city,
        hotels: cityHotels.slice().sort((left, right) =>
          String(left.hotelCode || left.hotelName || "").localeCompare(
            String(right.hotelCode || right.hotelName || ""),
          ),
        ),
      }));
  }, []);

  const hotelCityGroups = useMemo(() => hotelsByCity(hotels), [hotels, hotelsByCity]);
  const roomCategoryOptions = useMemo(
    () =>
      hotelCityGroups.map((group) => ({
        city: group.city,
        options: group.hotels.flatMap((hotel) =>
          (hotel.roomCategories || []).map((category) => ({
            value: category.roomCategoryId,
            label: [hotel.hotelCode, category.categoryName].filter(Boolean).join(" - "),
            totalUnits: category.totalUnits,
          })),
        ),
      })),
    [hotelCityGroups],
  );
  const flatRoomCategoryOptions = useMemo(
    () => roomCategoryOptions.flatMap((group) => group.options),
    [roomCategoryOptions],
  );
  const hotelOptions = useMemo(
    () =>
      hotelCityGroups.map((group) => ({
        city: group.city,
        options: group.hotels.map((hotel) => ({
          value: hotel.hotelId,
          label: [hotel.hotelCode, hotel.hotelName].filter(Boolean).join(" - "),
        })),
      })),
    [hotelCityGroups],
  );
  const roomCategoryOptionsForSelectedHotel = useMemo(() => {
    const hotel = hotels.find((item) => String(item.hotelId) === String(selectedHotelId));
    return (hotel?.roomCategories || []).map((category) => ({
      value: category.roomCategoryId,
      label: category.categoryName,
      totalUnits: category.totalUnits,
    }));
  }, [hotels, selectedHotelId]);

  const filteredHotels = useMemo(() => {
    if (!searchHotelId) return hotels;
    return hotels.filter((hotel) => String(hotel.hotelId) === String(searchHotelId));
  }, [hotels, searchHotelId]);
  const filteredHotelCityGroups = useMemo(
    () => hotelsByCity(filteredHotels),
    [filteredHotels, hotelsByCity],
  );

  const selectedCategory = flatRoomCategoryOptions.find((item) => String(item.value) === String(edit.roomCategoryId));
  const selectedCategoryStatus = useMemo(() => {
    if (!edit.roomCategoryId) return null;
    for (const hotel of hotels) {
      const category = (hotel.roomCategories || []).find(
        (item) => String(item.roomCategoryId) === String(edit.roomCategoryId),
      );
      if (category) {
        return { hotel, category };
      }
    }
    return null;
  }, [hotels, edit.roomCategoryId]);
  const [statusPanelDismissed, setStatusPanelDismissed] = useState(false);
  useEffect(() => {
    setStatusPanelDismissed(false);
  }, [edit.roomCategoryId]);
  const maxUnits = Number(selectedCategory?.totalUnits || 0);
  const selectedDays = selectedCategoryStatus?.category?.days || [];
  const selectedSoldDays = selectedDays.filter((day) => Number(day.soldUnits || 0) > 0).length;
  const selectedMinAvailable = selectedDays.length
    ? Math.min(...selectedDays.map((day) => Number(day.availableUnits || 0)))
    : Number(selectedCategoryStatus?.category?.availableUnits || 0);

  const soldUnitsForTarget = useCallback((day, soldTo) => (
    soldTo === "OTHERS"
      ? Number(day?.soldToOthersUnits || 0)
      : Number(day?.soldToIotripsUnits || 0)
  ), []);

  const quickEditKey = (roomCategoryId, date, soldTo) => `${roomCategoryId}:${date}:${soldTo}`;

  const quickEditValue = (roomCategoryId, day, soldTo) => {
    const key = quickEditKey(roomCategoryId, day.date, soldTo);
    return quickEdits[key] ?? soldUnitsForTarget(day, soldTo);
  };

  const setQuickEditValue = (roomCategoryId, date, soldTo, value) => {
    setQuickEdits((current) => ({
      ...current,
      [quickEditKey(roomCategoryId, date, soldTo)]: value,
    }));
  };

  const quickEditLimit = (category, day, soldTo) => {
    const otherBucket = soldTo === "OTHERS"
      ? Number(day?.soldToIotripsUnits || 0)
      : Number(day?.soldToOthersUnits || 0);
    // Rooms held for Iotrips cannot be sold to anyone else.
    const held = Number(day?.blockedUnits || 0);
    return Math.max(0, Number(category?.totalUnits || 0) - otherBucket - held);
  };

  const adjustQuickEditValue = (category, day, soldTo, delta) => {
    const currentValue = Number(quickEditValue(category.roomCategoryId, day, soldTo) || 0);
    const nextValue = Math.min(quickEditLimit(category, day, soldTo), Math.max(0, currentValue + delta));
    setQuickEditValue(category.roomCategoryId, day.date, soldTo, nextValue);
  };

  const hasQuickEdit = (roomCategoryId, day, soldTo) => {
    const key = quickEditKey(roomCategoryId, day.date, soldTo);
    return Object.prototype.hasOwnProperty.call(quickEdits, key)
      && Number(quickEdits[key] || 0) !== soldUnitsForTarget(day, soldTo);
  };

  const saveQuickSoldUnits = async (category, day, soldTo) => {
    const roomCategoryId = category?.roomCategoryId;
    if (!roomCategoryId) {
      toast.error("Select a room category");
      return;
    }
    const nextSoldUnits = Number(quickEditValue(roomCategoryId, day, soldTo) || 0);
    if (nextSoldUnits < 0) {
      toast.error("Sold rooms cannot be negative");
      return;
    }
    const otherBucket = soldTo === "OTHERS"
      ? Number(day.soldToIotripsUnits || 0)
      : Number(day.soldToOthersUnits || 0);
    if (otherBucket + nextSoldUnits > Number(category.totalUnits || 0)) {
      toast.error("Sold rooms cannot exceed total rooms");
      return;
    }
    const currentSoldUnits = soldUnitsForTarget(day, soldTo);
    let note = day.note || "";
    if (nextSoldUnits < currentSoldUnits) {
      note = window.prompt("Note is required when reducing sold rooms", note) || "";
      if (!note.trim()) {
        toast.error("Note is required when reducing sold rooms");
        return;
      }
    }

    const key = quickEditKey(roomCategoryId, day.date, soldTo);
    setQuickSaving(key);
    try {
      const updatedAvailability = await updateAvailability({
        roomCategoryId: Number(roomCategoryId),
        startDate: day.date,
        endDate: addDays(day.date, 1),
        soldUnits: nextSoldUnits,
        soldTo,
        note,
      });
      setHotels((current) => mergeAvailabilityUpdates(current, updatedAvailability));
      setQuickEdits((current) => {
        const next = { ...current };
        delete next[key];
        return next;
      });
      await loadAvailability(startDate, endDate);
      toast.success("Sold rooms updated");
    } catch (error) {
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        onUnauthorized?.();
      }
      toast.error(error?.response?.data?.message || "Failed to update sold rooms");
    } finally {
      setQuickSaving("");
    }
  };

  const selectAvailabilityDay = (roomCategoryId, day) => {
    const soldTo = "OTHERS";
    const owningHotel = hotels.find((hotel) =>
      (hotel.roomCategories || []).some((category) => String(category.roomCategoryId) === String(roomCategoryId)));
    if (owningHotel) setSelectedHotelId(String(owningHotel.hotelId));
    setEdit({
      roomCategoryId: String(roomCategoryId),
      startDate: day.date,
      endDate: addDays(day.date, 1),
      soldUnits: soldUnitsForTarget(day, soldTo),
      soldTo,
      note: day.note || "",
    });
    setStatusPanelDismissed(false);
    setEditSectionOverride(true);
    editFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const toggleCity = (city) => {
    setCollapsedCities((prev) => ({ ...prev, [city]: !(prev[city] ?? manyHotels) }));
  };

  const setDateWindow = (windowStart) => {
    const latestWindowStart = addDays(maximumDateValue(), -14);
    const safeStart = windowStart < todayValue()
      ? todayValue()
      : windowStart > latestWindowStart
        ? latestWindowStart
        : windowStart;
    setStartDate(safeStart);
    setEndDate(addDays(safeStart, 14));
  };

  const navigateDateWindow = (days) => {
    setDateWindow(addDays(startDate, days));
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!edit.roomCategoryId) {
      toast.error("Select a room category");
      return;
    }
    if (edit.startDate < todayValue() || edit.endDate > addDays(maximumDateValue(), 1)) {
      toast.error("Availability can only be updated from today through the next 90 days");
      return;
    }
    if (edit.endDate <= edit.startDate) {
      toast.error("Check-out must be after check-in");
      return;
    }
    const selectedRangeDays = selectedDays.filter((day) => day.date >= edit.startDate && day.date < edit.endDate);
    const soldTo = "OTHERS";
    const reducingSoldUnits = selectedRangeDays.some(
      (day) => soldUnitsForTarget(day, soldTo) > Number(edit.soldUnits || 0),
    );
    if (reducingSoldUnits && !edit.note.trim()) {
      toast.error("Note is required when reducing sold rooms");
      return;
    }
    const exceedsTotalUnits = selectedRangeDays.some((day) => {
      const otherBucket = soldTo === "OTHERS"
        ? Number(day.soldToIotripsUnits || 0)
        : Number(day.soldToOthersUnits || 0);
      return otherBucket + Number(edit.soldUnits || 0) > maxUnits;
    });
    if (Number(edit.soldUnits || 0) > maxUnits || exceedsTotalUnits) {
      toast.error("Sold rooms cannot exceed total rooms");
      return;
    }
    setSaving(true);
    try {
      const updatedAvailability = await updateAvailability({
        ...edit,
        roomCategoryId: Number(edit.roomCategoryId),
        soldUnits: Number(edit.soldUnits || 0),
        soldTo,
      });
      setHotels((current) => mergeAvailabilityUpdates(current, updatedAvailability));
      await loadAvailability(startDate, endDate);
      toast.success("Room availability updated");
    } catch (error) {
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        onUnauthorized?.();
      }
      toast.error(error?.response?.data?.message || "Failed to update room availability");
    } finally {
      setSaving(false);
    }
  };

  const renderEditableDay = (category, day, { openDetailedEditor = false } = {}) => {
    const sold = Number(day.soldUnits || 0);
    const soldToIotrips = Number(day.soldToIotripsUnits || 0);
    const soldToOthers = Number(day.soldToOthersUnits || 0);
    const available = Number(day.availableUnits || 0);
    const categoryId = category.roomCategoryId;
    const savingKey = quickEditKey(categoryId, day.date, "OTHERS");
    const currentValue = Number(quickEditValue(categoryId, day, "OTHERS") || 0);
    const dirty = hasQuickEdit(categoryId, day, "OTHERS");
    const openEditor = () => {
      if (openDetailedEditor) selectAvailabilityDay(categoryId, day);
    };

    return (
      <div
        key={day.date}
        role={openDetailedEditor ? "button" : undefined}
        tabIndex={openDetailedEditor ? 0 : undefined}
        className={`hotel-status-day ${available <= 0 ? "is-full" : ""} ${sold > 0 ? "has-hold" : ""}`}
        onClick={openDetailedEditor ? openEditor : undefined}
        onKeyDown={openDetailedEditor ? (event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openEditor();
          }
        } : undefined}
      >
        <div className="hotel-status-day-summary">
          <span className="hotel-status-date">{formatDateDMY(day.date)}</span>
          <strong>{available} available</strong>
        </div>
        {Number(day.blockedUnits || 0) > 0 && (
          <div className="small fw-semibold text-primary">
            {Number(day.blockedUnits)} held for Iotrips
          </div>
        )}
        {soldToIotrips > 0 && (
          <SoldUnitsLabel count={soldToIotrips} label="sold to Iotrips" bookings={day.iotripsBookings} />
        )}
        {soldToOthers > 0 && <SoldUnitsLabel count={soldToOthers} label="sold to others" />}
        <div
          className="hotel-status-quick-edit"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          <span className="hotel-status-quick-label">Sold to others</span>
          <div className="hotel-status-stepper">
            <Button
              type="button"
              variant="outline-secondary"
              aria-label={`Reduce rooms sold to others on ${formatDateDMY(day.date)}`}
              disabled={currentValue <= 0 || quickSaving === savingKey}
              onClick={() => adjustQuickEditValue(category, day, "OTHERS", -1)}
            >
              <i className="bi bi-dash-lg" aria-hidden="true" />
            </Button>
            <Form.Control
              type="number"
              min="0"
              max={quickEditLimit(category, day, "OTHERS")}
              inputMode="numeric"
              aria-label={`Rooms sold to others on ${formatDateDMY(day.date)}`}
              value={quickEditValue(categoryId, day, "OTHERS")}
              onChange={(event) => setQuickEditValue(categoryId, day.date, "OTHERS", event.target.value)}
            />
            <Button
              type="button"
              variant="outline-secondary"
              aria-label={`Add a room sold to others on ${formatDateDMY(day.date)}`}
              disabled={currentValue >= quickEditLimit(category, day, "OTHERS") || quickSaving === savingKey}
              onClick={() => adjustQuickEditValue(category, day, "OTHERS", 1)}
            >
              <i className="bi bi-plus-lg" aria-hidden="true" />
            </Button>
          </div>
          {dirty && (
            <Button
              type="button"
              variant="primary"
              className="hotel-status-save"
              disabled={quickSaving === savingKey}
              onClick={() => saveQuickSoldUnits(category, day, "OTHERS")}
            >
              {quickSaving === savingKey ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <><i className="bi bi-check-lg" aria-hidden="true" /> Save change</>
              )}
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <Container fluid className="py-3 hotel-availability-admin">
      <Row className="align-items-center g-3 mb-3 hotel-availability-page-header">
        <Col>
          <h4 className="mb-0 hotel-availability-title">{title}</h4>
          <div className="small hotel-availability-subtitle">{subtitle}</div>
        </Col>
        {headerAction && <Col xs="auto">{headerAction}</Col>}
        <Col md={2}>
          <Form.Label className="small text-muted mb-1">{directManagementView ? "Starting date" : "Window Start"}</Form.Label>
          <IsoDatePicker min={todayValue()} max={addDays(maximumDateValue(), -14)} value={startDate} onChange={setDateWindow} />
        </Col>
        {!directManagementView && <Col md={2}>
          <Form.Label className="small text-muted mb-1">Window End</Form.Label>
          <Form.Control value={formatDateDMY(endDate)} readOnly aria-label="15-day window end date" />
        </Col>}
        {hotels.length > 1 && (
          <Col md={3}>
            <Form.Label className="small text-muted mb-1">Filter by Hotel</Form.Label>
            <Form.Select aria-label="Search hotel" value={searchHotelId} onChange={(event) => setSearchHotelId(event.target.value)}>
              <option value="">All hotels</option>
              {hotelOptions.map((group) => (
                <optgroup key={group.city} label={group.city}>
                  {group.options.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </Form.Select>
          </Col>
        )}
      </Row>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3 hotel-date-navigation">
        <Button
          type="button"
          size="sm"
          variant="outline-primary"
          disabled={startDate <= todayValue()}
          onClick={() => navigateDateWindow(-15)}
        >
          <i className="bi bi-chevron-left me-1" aria-hidden="true" />
          {directManagementView ? "Previous" : "Previous 15 Days"}
        </Button>
        <Button type="button" size="sm" variant="outline-secondary" onClick={() => setDateWindow(todayValue())}>
          {directManagementView ? "Today" : "Current 15 Days"}
        </Button>
        <Button type="button" size="sm" variant="outline-primary" disabled={endDate >= maximumDateValue()} onClick={() => navigateDateWindow(15)}>
          {directManagementView ? "Next" : "Next 15 Days"}
          <i className="bi bi-chevron-right ms-1" aria-hidden="true" />
        </Button>
        <span className="small text-muted ms-sm-2">
          Showing {formatDateDMY(startDate)} to {formatDateDMY(endDate)}
        </span>
        {showOwnerAccess && (
          <Button
            type="button"
            size="sm"
            variant="primary"
            className="ms-sm-auto"
            onClick={openReminderModal}
            disabled={!hotels.length}
          >
            <i className="bi bi-send-check me-1" aria-hidden="true" />
            Send Booking Reminders
          </Button>
        )}
      </div>

      {showOwnerAccess && <PendingHotelAcceptances />}
      {showOwnerAccess && (
        <RoomHoldsManager
          hotels={hotels}
          createForHotel={holdCreateHotel}
          onCloseCreate={() => setHoldCreateHotel(null)}
          onChanged={() => loadAvailability(startDate, endDate)}
        />
      )}
      {showOwnerAccess && <HotelOwnerAccessManager hotels={hotels} />}

      {!directManagementView && !readOnly && <Card ref={editFormRef} className="mb-3 shadow-sm" style={{ scrollMarginTop: "90px" }}>
        <Card.Header
          className="d-flex align-items-center gap-2"
          role="button"
          onClick={() => setEditSectionOverride(!editSectionExpanded)}
          aria-expanded={editSectionExpanded}
        >
          <i className={`bi ${editSectionExpanded ? "bi-chevron-down" : "bi-chevron-right"}`} aria-hidden="true" />
          <span className="fw-semibold">Update Room Availability</span>
          {!editSectionExpanded && selectedCategoryStatus && (
            <span className="small text-muted">
              &mdash; {[selectedCategoryStatus.hotel.hotelCode || selectedCategoryStatus.hotel.hotelName, selectedCategoryStatus.category.categoryName]
                .filter(Boolean)
                .join(" - ")}
            </span>
          )}
        </Card.Header>
        {editSectionExpanded && (
        <Card.Body>
          <Form onSubmit={submit}>
            <Row className="g-3 align-items-end">
              <Col md={3}>
                <Form.Label>Hotel</Form.Label>
                <Form.Select
                  value={selectedHotelId}
                  onChange={(event) => {
                    setSelectedHotelId(event.target.value);
                    setEdit((prev) => ({ ...prev, roomCategoryId: "" }));
                  }}
                >
                  <option value="">Select hotel</option>
                  {hotelOptions.map((group) => (
                    <optgroup key={group.city} label={group.city}>
                      {group.options.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Label>Room Category</Form.Label>
                <Form.Select
                  value={edit.roomCategoryId}
                  onChange={(event) => setEdit((prev) => ({ ...prev, roomCategoryId: event.target.value }))}
                  disabled={!selectedHotelId}
                >
                  <option value="">{selectedHotelId ? "Select room category" : "Select a hotel first"}</option>
                  {roomCategoryOptionsForSelectedHotel.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label} ({option.totalUnits} rooms)
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={2}>
                <Form.Label>Check-in</Form.Label>
                <IsoDatePicker min={todayValue()} max={maximumDateValue()} value={edit.startDate} onChange={(value) => setEdit((prev) => ({ ...prev, startDate: value }))} />
              </Col>
              <Col md={2}>
                <Form.Label>Check-out</Form.Label>
                <IsoDatePicker min={addDays(edit.startDate || todayValue(), 1)} max={addDays(maximumDateValue(), 1)} value={edit.endDate} onChange={(value) => setEdit((prev) => ({ ...prev, endDate: value }))} />
              </Col>
              <Col md={2}>
                <Form.Label>Sold to others</Form.Label>
                <InputGroup>
                  <Form.Control type="number" min="0" max={maxUnits} value={edit.soldUnits} onChange={(event) => setEdit((prev) => ({ ...prev, soldUnits: event.target.value }))} />
                  <InputGroup.Text>rooms</InputGroup.Text>
                </InputGroup>
              </Col>
              <Col lg={6}>
                <Form.Label>Note</Form.Label>
                <Form.Control value={edit.note} onChange={(event) => setEdit((prev) => ({ ...prev, note: event.target.value }))} placeholder="Required when reducing sold rooms" />
              </Col>
              <Col className="d-flex gap-2">
                <Button type="submit" disabled={saving || !edit.roomCategoryId}>
                  {saving ? <Spinner animation="border" size="sm" /> : "Update"}
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
        )}
      </Card>}

      {!directManagementView && !readOnly && selectedCategoryStatus && !statusPanelDismissed && (
        <Card className="mb-3 shadow-sm hotel-status-card">
          <Card.Body>
            <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
              <div>
                <div className="fw-semibold">Current Status</div>
                <div className="text-muted small">
                  {[selectedCategoryStatus.hotel.city, selectedCategoryStatus.hotel.hotelCode, selectedCategoryStatus.category.categoryName]
                    .filter(Boolean)
                    .join(" - ")}
                </div>
              </div>
              <div className="d-flex flex-wrap align-items-start gap-2">
                <Badge bg="secondary">Total {selectedCategoryStatus.category.totalUnits}</Badge>
                <Badge bg={selectedMinAvailable > 0 ? "success" : "danger"}>Min available {selectedMinAvailable}</Badge>
                <Badge bg={selectedSoldDays > 0 ? "primary" : "light"} text={selectedSoldDays > 0 ? undefined : "dark"}>
                  Sold dates {selectedSoldDays}
                </Badge>
                <Button
                  type="button"
                  size="sm"
                  variant="outline-secondary"
                  aria-label="Close current status panel"
                  onClick={() => setStatusPanelDismissed(true)}
                >
                  <i className="bi bi-x-lg" aria-hidden="true" />
                </Button>
              </div>
            </div>
            <div className="hotel-status-days">
              {selectedDays.map((day) => renderEditableDay(selectedCategoryStatus.category, day, { openDetailedEditor: true }))}
            </div>
          </Card.Body>
        </Card>
      )}

      {loading && <Alert variant="info">Loading hotel availability...</Alert>}
      {!loading && filteredHotels.length === 0 && <Alert variant="secondary">No hotels found.</Alert>}

      <div className="d-flex flex-column gap-4">
        {filteredHotelCityGroups.map((group) => (
          <section key={group.city} className="hotel-city-group">
            <button
              type="button"
              className="hotel-city-heading"
              onClick={() => toggleCity(group.city)}
              aria-expanded={!(collapsedCities[group.city] ?? manyHotels)}
            >
              <span className="d-flex align-items-center gap-2">
                <i
                  className={`bi ${(collapsedCities[group.city] ?? manyHotels) ? "bi-chevron-right" : "bi-chevron-down"}`}
                  aria-hidden="true"
                />
                {group.city}
              </span>
              <Badge bg="light" text="dark">{group.hotels.length} hotels</Badge>
            </button>
            {!(collapsedCities[group.city] ?? manyHotels) && <div className="d-flex flex-column gap-3">
            {group.hotels.map((hotel) => (
              <Card key={hotel.hotelId} className="shadow-sm">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>
                <div className="fw-semibold hotel-availability-hotel-code">{hotel.hotelCode}</div>
                {hotel.hotelName && <div className="small hotel-availability-hotel-name">{hotel.hotelName}</div>}
              </div>
              <div className="d-flex align-items-center gap-2">
                {showOwnerAccess && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline-primary"
                    onClick={() => setHoldCreateHotel(hotel)}
                  >
                    <i className="bi bi-lock me-1" aria-hidden="true" />
                    Hold Rooms
                  </Button>
                )}
                {showSoldOutActions && <>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline-danger"
                    onClick={() => openSoldOutModal(hotel, "CLOSE")}
                  >
                    <i className="bi bi-slash-circle me-1" aria-hidden="true" />
                    Mark Sold Out
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline-success"
                    onClick={() => openSoldOutModal(hotel, "REOPEN")}
                  >
                    <i className="bi bi-arrow-counterclockwise me-1" aria-hidden="true" />
                    Undo Sold Out
                  </Button>
                </>}
                {showCopySummary && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline-primary"
                    onClick={() => openSummaryModal(hotel)}
                  >
                    <i className="bi bi-clipboard-check me-1" aria-hidden="true" />
                    Copy Summary
                  </Button>
                )}
                <Badge bg="light" text="dark">{hotel.roomCategories.length} room types</Badge>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {directManagementView ? (
                <div className="hotel-direct-management">
                  {hotel.roomCategories.map((category, categoryIndex) => {
                    const categoryKey = String(category.roomCategoryId);
                    const categoryDays = category.days || [];
                    const soldDates = categoryDays.filter((day) => Number(day.soldUnits || 0) > 0).length;
                    const minAvailable = categoryDays.length
                      ? Math.min(...categoryDays.map((day) => Number(day.availableUnits || 0)))
                      : Number(category.availableUnits || 0);
                    const collapsed = collapsedRoomCategories[categoryKey] ?? categoryIndex > 0;
                    return (
                      <section className="hotel-direct-room" key={category.roomCategoryId}>
                        <button
                          type="button"
                          className="hotel-direct-room-header"
                          aria-expanded={!collapsed}
                          onClick={() => setCollapsedRoomCategories((current) => ({
                            ...current,
                            [categoryKey]: !(current[categoryKey] ?? categoryIndex > 0),
                          }))}
                        >
                          <span className="hotel-direct-room-title">
                            <i className={`bi ${collapsed ? "bi-chevron-right" : "bi-chevron-down"}`} aria-hidden="true" />
                            <span>{category.categoryName}</span>
                          </span>
                          <span className="hotel-direct-room-stats">
                            <span>{category.totalUnits} total</span>
                            <span className={minAvailable > 0 ? "is-available" : "is-full"}>{minAvailable} min available</span>
                            <span>{soldDates} sold dates</span>
                          </span>
                        </button>
                        {!collapsed && (
                          <div className="hotel-status-days hotel-status-days--direct">
                            {categoryDays.map((day) => renderEditableDay(category, day))}
                          </div>
                        )}
                      </section>
                    );
                  })}
                </div>
              ) : (
              <Table responsive hover className="mb-0 align-middle">
                <thead>
                  <tr>
                    <th className="sticky-col">Room Type</th>
                    <th>Total</th>
                    <th>Available</th>
                    {(hotel.roomCategories[0]?.days || []).map((day) => {
                      const { weekday } = formatShortDayLabel(day.date);
                      return (
                        <th key={day.date} className={`text-nowrap ${day.date === todayValue() ? "is-today" : ""}`}>
                          <div>{formatDateDMY(day.date)}</div>
                          <div className="small text-muted fw-normal">{weekday}</div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {hotel.roomCategories.map((category) => (
                    <tr key={category.roomCategoryId}>
                      <td className="fw-semibold sticky-col">{category.categoryName}</td>
                      <td>{category.totalUnits}</td>
                      <td>{category.availableUnits}</td>
                      {(category.days || []).map((day) => {
                        const held = Number(day.blockedUnits || 0);
                        // Held rooms are closed to the hotel's other buyers but ours to sell, so the
                        // sales teams (read-only view) see them as available; admin sees them apart.
                        const shownAvailable = Number(day.availableUnits || 0) + (readOnly ? held : 0);
                        const cellContent = (
                          <>
                            <span className={shownAvailable > 0 ? "text-success" : "text-danger"}>{shownAvailable} available</span>
                            {held > 0 && (
                              <div className="small fw-semibold text-primary">
                                {readOnly ? `incl. ${held} held for us` : `${held} held for Iotrips`}
                              </div>
                            )}
                            {day.soldUnits > 0 && (
                              <div className="small text-muted">
                                {Number(day.soldToIotripsUnits || 0) > 0 && (
                                  <SoldUnitsLabel count={day.soldToIotripsUnits} label="sold to Iotrips" note={day.note} bookings={day.iotripsBookings} />
                                )}
                                {Number(day.soldToOthersUnits || 0) > 0 && (
                                  <SoldUnitsLabel count={day.soldToOthersUnits} label="sold to others" note={day.note} />
                                )}
                              </div>
                            )}
                          </>
                        );
                        return (
                          <td key={day.date} className={day.date === todayValue() ? "is-today" : ""}>
                            {readOnly ? (
                              <div className="availability-cell is-readonly">{cellContent}</div>
                            ) : (
                              <button
                                type="button"
                                className={`availability-cell ${
                                  String(edit.roomCategoryId) === String(category.roomCategoryId)
                                  && edit.startDate === day.date
                                  && edit.endDate === addDays(day.date, 1)
                                    ? "is-selected"
                                    : ""
                                }`}
                                onClick={() => selectAvailabilityDay(category.roomCategoryId, day)}
                                aria-label={`Edit ${category.categoryName} availability for ${formatDateDMY(day.date)}`}
                              >
                                {cellContent}
                              </button>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </Table>
              )}
            </Card.Body>
          </Card>
            ))}
            </div>
            }
          </section>
        ))}
      </div>

      <Modal show={Boolean(summaryModal)} onHide={closeSummaryModal} centered size={isReminderMode ? "lg" : undefined}>
        <Modal.Header closeButton={!summaryLoading}>
          <Modal.Title>
            {isReminderMode ? "Send Booking Reminders" : "Copy Booking Summary"}
            {summaryModal?.hotel && (
              <div className="small text-muted fw-normal">
                {summaryModal.hotel.hotelCode || summaryModal.hotel.hotelName}
              </div>
            )}
            {isReminderMode && (
              <div className="small text-muted fw-normal">
                One message per hotel, so their rooms are not given away
              </div>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Date Range</Form.Label>
            <Form.Select
              value={summaryPreset}
              disabled={summaryLoading}
              onChange={(event) => handleSummaryPresetChange(event.target.value)}
            >
              {SUMMARY_RANGE_PRESETS.map((preset) => (
                <option key={preset.id} value={preset.id}>
                  {preset.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Row className="g-3">
            <Col xs={6}>
              <Form.Label>Start Date</Form.Label>
              <IsoDatePicker
                value={summaryStartDate}
                min={todayValue()}
                max={maximumDateValue()}
                disabled={summaryPreset !== "CUSTOM" || summaryLoading}
                onChange={setSummaryStartDate}
              />
            </Col>
            <Col xs={6}>
              <Form.Label>End Date</Form.Label>
              <IsoDatePicker
                value={summaryEndDate}
                min={summaryStartDate || todayValue()}
                max={maximumDateValue()}
                disabled={summaryPreset !== "CUSTOM" || summaryLoading}
                onChange={setSummaryEndDate}
              />
            </Col>
          </Row>
          {summaryStartDate && summaryEndDate && (
            <div className="small text-muted mt-2">
              Showing {formatDateDMY(summaryStartDate)} to {formatDateDMY(summaryEndDate)}
            </div>
          )}

          {isReminderMode && !reminderResults && (
            <div className="mt-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Form.Label className="mb-0">Hotels</Form.Label>
                <div className="d-flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="link"
                    className="p-0"
                    disabled={summaryLoading}
                    onClick={() => setReminderSelectedIds(hotels.map((hotel) => String(hotel.hotelId)))}
                  >
                    Select all
                  </Button>
                  <span className="text-muted">|</span>
                  <Button
                    type="button"
                    size="sm"
                    variant="link"
                    className="p-0"
                    disabled={summaryLoading}
                    onClick={() => setReminderSelectedIds([])}
                  >
                    Clear
                  </Button>
                </div>
              </div>
              <div className="border rounded p-2" style={{ maxHeight: 280, overflowY: "auto" }}>
                {reminderHotelGroups.map((group) => (
                  <div key={group.city} className="mb-2">
                    <div className="small fw-semibold text-muted">{group.city}</div>
                    {group.hotels.map((hotel) => (
                      <Form.Check
                        key={hotel.hotelId}
                        type="checkbox"
                        id={`reminder-hotel-${hotel.hotelId}`}
                        className="ms-2"
                        disabled={summaryLoading}
                        checked={reminderSelectedIds.includes(String(hotel.hotelId))}
                        onChange={() => toggleReminderHotel(hotel.hotelId)}
                        label={
                          <span>
                            {hotel.hotelName || hotel.hotelCode}
                            {!reminderWhatsappNumber(hotel.caretakerNumber) && (
                              <span className="text-muted small"> &mdash; no caretaker number</span>
                            )}
                          </span>
                        }
                      />
                    ))}
                  </div>
                ))}
                {!reminderHotelGroups.length && (
                  <div className="text-muted small">No hotels loaded for this date window.</div>
                )}
              </div>
              <div className="small text-muted mt-2">
                {reminderSelectedIds.length} of {hotels.length} hotels selected. Hotels with no
                bookings in the range are skipped automatically.
              </div>
              <Form.Check
                type="checkbox"
                id="reminder-skip-checked-in"
                className="mt-3"
                disabled={summaryLoading}
                checked={reminderSkipCheckedIn}
                onChange={(event) => setReminderSkipCheckedIn(event.target.checked)}
                label={
                  <span>
                    Skip guests who have already checked in
                    <div className="small text-muted">
                      A guest already in the room cannot have it given away, so their nights are left
                      out, and a hotel with only such guests is not messaged. Room moves, category
                      changes, extra rooms and new arrivals are still sent.
                    </div>
                  </span>
                }
              />
            </div>
          )}

          {isReminderMode && reminderResults && (
            <div className="mt-3 d-flex flex-column gap-2">
              <div className="small text-muted">
                {reminderResults.items.length} hotel{reminderResults.items.length === 1 ? "" : "s"} to
                message
                {reminderResults.skipped
                  ? ` \u00b7 ${reminderResults.skipped} skipped with no bookings`
                  : ""}
                {reminderResults.alreadyCheckedIn?.length
                  ? ` · ${reminderResults.alreadyCheckedIn.length} skipped, guests already checked in`
                  : ""}
              </div>
              {Boolean(reminderResults.checkedInUnavailable) && (
                <div className="alert alert-warning py-2 px-3 mb-0 small">
                  Could not check which guests are already in-house, so every hotel with bookings is
                  listed below. This clears once the server update is live.
                </div>
              )}
              {Boolean(reminderResults.alreadyCheckedIn?.length) && (
                <div className="border rounded bg-light p-2">
                  <div className="small fw-semibold">Skipped - guests already checked in</div>
                  <div className="small text-muted">
                    {reminderResults.alreadyCheckedIn
                      .map((hotel) => hotel.hotelName || hotel.hotelCode)
                      .join(", ")}
                  </div>
                  <div className="small text-muted mt-1">
                    Untick the skip option and generate again if any of these still need a message.
                  </div>
                </div>
              )}
              {reminderResults.items.map((item) => {
                const number = reminderWhatsappNumber(item.hotel.caretakerNumber);
                return (
                  <Card key={item.hotel.hotelId} className="shadow-sm">
                    <Card.Body className="p-2">
                      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                        <div>
                          <div className="fw-semibold">{item.hotel.hotelName || item.hotel.hotelCode}</div>
                          <div className="small text-muted">
                            {[item.hotel.city, item.hotel.caretakerName, item.hotel.caretakerNumber]
                              .filter(Boolean)
                              .join(" \u00b7 ") || "No caretaker contact saved"}
                          </div>
                        </div>
                        <div className="d-flex gap-2">
                          <Button type="button" size="sm" variant="outline-primary" onClick={() => copyReminder(item)}>
                            <i className="bi bi-clipboard-check me-1" aria-hidden="true" />
                            Copy
                          </Button>
                          {number ? (
                            <Button
                              as="a"
                              size="sm"
                              variant="success"
                              href={`https://wa.me/${number}?text=${encodeURIComponent(item.text)}`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <i className="bi bi-whatsapp me-1" aria-hidden="true" />
                              WhatsApp
                            </Button>
                          ) : (
                            <Button type="button" size="sm" variant="outline-secondary" disabled>
                              No number
                            </Button>
                          )}
                        </div>
                      </div>
                      <pre className="small bg-light border rounded p-2 mt-2 mb-0" style={{ whiteSpace: "pre-wrap" }}>
                        {item.text}
                      </pre>
                    </Card.Body>
                  </Card>
                );
              })}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeSummaryModal} disabled={summaryLoading}>
            {reminderResults ? "Done" : "Cancel"}
          </Button>
          {isReminderMode && reminderResults && (
            <Button variant="outline-primary" onClick={() => setReminderResults(null)} disabled={summaryLoading}>
              Back
            </Button>
          )}
          {!reminderResults && (
            <Button
              variant="primary"
              onClick={isReminderMode ? handleGenerateReminders : handleGenerateSummary}
              disabled={summaryLoading}
            >
              {summaryLoading
                ? <Spinner animation="border" size="sm" />
                : (isReminderMode ? "Generate Reminders" : "Copy Summary")}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {showSoldOutActions && <Modal show={Boolean(soldOutModal)} onHide={closeSoldOutModal} centered>
        <Modal.Header closeButton={!soldOutSaving}>
          <Modal.Title>
            {soldOutModal?.mode === "REOPEN" ? "Undo Hotel Sold Out" : "Mark Hotel Sold Out"}
            {soldOutModal?.hotel && (
              <div className="small text-muted fw-normal">
                {soldOutModal.hotel.hotelCode || soldOutModal.hotel.hotelName}
              </div>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col xs={6}>
              <Form.Label>Check-in</Form.Label>
              <IsoDatePicker
                value={soldOutStartDate}
                min={todayValue()}
                max={maximumDateValue()}
                disabled={soldOutSaving}
                onChange={(value) => {
                  setSoldOutStartDate(value);
                  if (soldOutEndDate <= value) setSoldOutEndDate(addDays(value, 1));
                }}
              />
            </Col>
            <Col xs={6}>
              <Form.Label>Check-out</Form.Label>
              <IsoDatePicker
                value={soldOutEndDate}
                min={addDays(soldOutStartDate || todayValue(), 1)}
                max={addDays(maximumDateValue(), 1)}
                disabled={soldOutSaving}
                onChange={setSoldOutEndDate}
              />
            </Col>
          </Row>
          {soldOutModal?.mode !== "REOPEN" && (
            <Form.Group className="mt-3">
              <Form.Label>Note <span className="text-muted small">(optional)</span></Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={soldOutNote}
                disabled={soldOutSaving}
                placeholder="Why the hotel is closed for these dates"
                onChange={(event) => setSoldOutNote(event.target.value)}
              />
            </Form.Group>
          )}
          {soldOutModal?.mode === "REOPEN" ? (
            <Alert variant="info" className="small mt-3 mb-0">
              Each date closed by a whole-hotel sold-out goes back to the sold rooms it held before
              it was closed, for{" "}
              <strong>{soldOutNights} night{soldOutNights === 1 ? "" : "s"}</strong>
              {soldOutNights > 0 && (
                <> ({formatDateDMY(soldOutStartDate)} to {formatDateDMY(soldOutEndDate)})</>
              )}
              . Dates you have edited by hand since closing are left untouched, so this cannot
              revert that work.
            </Alert>
          ) : (
            <Alert variant="warning" className="small mt-3 mb-0">
              Every active room type in this hotel will show zero rooms available for{" "}
              <strong>{soldOutNights} night{soldOutNights === 1 ? "" : "s"}</strong>
              {soldOutNights > 0 && (
                <> ({formatDateDMY(soldOutStartDate)} to {formatDateDMY(soldOutEndDate)})</>
              )}
              . Rooms already sold through Iotrips stay recorded against their bookings, and
              Undo Sold Out puts these dates back.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeSoldOutModal} disabled={soldOutSaving}>
            Cancel
          </Button>
          <Button
            variant={soldOutModal?.mode === "REOPEN" ? "success" : "danger"}
            onClick={handleSoldOutSubmit}
            disabled={soldOutSaving || soldOutNights <= 0}
          >
            {soldOutSaving ? (
              <Spinner animation="border" size="sm" />
            ) : soldOutModal?.mode === "REOPEN" ? (
              "Undo Sold Out"
            ) : (
              "Mark Sold Out"
            )}
          </Button>
        </Modal.Footer>
      </Modal>}
    </Container>
  );
}
